import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download } from 'lucide-react'
import { AccountingSyncChip } from '@/components/AccountingSyncChip'
import { PeriodField, type Period } from '@/components/IncomeReportFilters'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, getJSON } from '@/lib/api'
import { ViewOnlyBadge } from '@/components/templates/app-shell'
import { OwnerFilter } from '@/components/OwnerFilter'
import { displayShortDate } from '@/lib/dates'

type CollectionsGrouping = 'payment_type' | 'month'

interface CollectionsPayment {
  id: number | string
  contact_name: string
  payment_type: string
  paid_on: string
  amount_cents: number
  amount: string
  online: boolean
  kind: 'payment' | 'refund' | 'chargeback'
}

interface CollectionsGroup {
  key: string
  label: string
  payment_count: number
  total_cents: number
  total: string
  payments: CollectionsPayment[]
}

interface CollectionsReport {
  period: Period
  grouping: CollectionsGrouping
  starts_on: string
  ends_on: string
  payment_count: number
  online_total_cents: number
  online_total: string
  offline_total_cents: number
  offline_total: string
  grand_total_cents: number
  grand_total: string
  groups: CollectionsGroup[]
}

interface CollectionsReportResponse {
  collections_report: CollectionsReport
}

interface RangeSelection {
  period: Period
  startsOn?: string
  endsOn?: string
  grouping: CollectionsGrouping
}

export function CollectionsReportPage() {
  const [form, setForm] = useState<RangeSelection>({
    period: 'this_month',
    grouping: 'payment_type',
  })
  const [selection, setSelection] = useState<RangeSelection>({
    period: 'this_month',
    grouping: 'payment_type',
  })
  const [owner, setOwner] = useState<string | undefined>()
  const reportQuery = useQuery({
    queryKey: [
      'collections-report',
      selection.period,
      selection.startsOn,
      selection.endsOn,
      selection.grouping,
      owner,
    ],
    queryFn: () =>
      getJSON<CollectionsReportResponse>(collectionsReportPath(selection, false, owner)),
    retry: false,
  })
  const report = reportQuery.data?.collections_report
  const startsOn = form.startsOn ?? report?.starts_on ?? ''
  const endsOn = form.endsOn ?? report?.ends_on ?? ''

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold">Collections report</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          Payments and money out by event date · Association clock
        </p>
      </header>
      <form
        className="mt-4 flex flex-wrap items-end justify-between gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setSelection({ ...form, startsOn, endsOn })
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <PeriodField
            value={form.period}
            onChange={(period) =>
              setForm({
                ...form,
                period,
                startsOn: period === 'custom' ? startsOn : undefined,
                endsOn: period === 'custom' ? endsOn : undefined,
              })
            }
          />
          <DateField
            label="From"
            value={startsOn}
            disabled={form.period !== 'custom'}
            onChange={(value) => setForm({ ...form, startsOn: value })}
          />
          <DateField
            label="To"
            value={endsOn}
            disabled={form.period !== 'custom'}
            onChange={(value) => setForm({ ...form, endsOn: value })}
          />
          <Button size="sm" type="submit">
            Apply
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <GroupingField
            value={form.grouping}
            onChange={(grouping) => setForm({ ...form, grouping })}
          />
          <OwnerFilter value={owner} onChange={setOwner} />
          <Button size="sm" variant="outline" asChild>
            <a href={collectionsReportPath(selection, true)} download>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </div>
      </form>
      <div className="mt-4 mb-2 flex items-center justify-between">
        <p className="text-caption text-muted-foreground">
          {report
            ? `${report.payment_count} ${report.payment_count === 1 ? 'payment' : 'payments'}`
            : '— payments'}
        </p>
        <AccountingSyncChip />
      </div>
      <ReportBody query={reportQuery} />
    </div>
  )
}

function GroupingField({
  value,
  onChange,
}: {
  value: CollectionsGrouping
  onChange: (value: CollectionsGrouping) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label>Group by</Label>
      <Select value={value} onValueChange={(next) => onChange(next as CollectionsGrouping)}>
        <SelectTrigger className="h-9 w-40" aria-label="Group by">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="payment_type">Payment type</SelectItem>
          <SelectItem value="month">Month</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

function DateField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const id = `collections-report-${label.toLowerCase()}`
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="h-9 w-40"
        type="date"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function ReportBody({ query }: { query: UseQueryResult<CollectionsReportResponse, Error> }) {
  if (query.isLoading) return <EmptyState title="Loading collections report" className="min-h-52" />
  if (query.isError) {
    return <EmptyState title={collectionsReportError(query.error)} className="min-h-52" />
  }

  const report = query.data?.collections_report
  if (!report || report.groups.length === 0) {
    return (
      <EmptyState
        className="mt-2 min-h-52"
        variant="quiet"
        title="No collections activity in this range."
        description="Adjust the period."
      />
    )
  }

  return <CollectionsTable report={report} />
}

function CollectionsTable({ report }: { report: CollectionsReport }) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Payment</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {report.groups.map((group) => (
            <CollectionsGroupRows key={group.key} group={group} />
          ))}
          <TotalRow label="Online total" amount={report.online_total} />
          <TotalRow label="Offline total" amount={report.offline_total} />
          <TotalRow label="Net collected" amount={report.grand_total} grand />
        </TableBody>
      </Table>
    </div>
  )
}

function CollectionsGroupRows({ group }: { group: CollectionsGroup }) {
  const [expanded, setExpanded] = useState(true)
  const Icon = expanded ? ChevronDown : ChevronRight

  return (
    <Fragment>
      <TableRow className="bg-muted hover:bg-muted">
        <TableCell className="pl-3 font-medium">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto border-0 bg-transparent p-0 font-medium hover:bg-transparent"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
            onClick={() => setExpanded((current) => !current)}
          >
            <Icon className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {group.label} ({group.payment_count})
          </Button>
        </TableCell>
        <TableCell />
      </TableRow>
      {expanded &&
        group.payments.map((payment) => (
          <TableRow key={payment.id}>
            <TableCell className="pl-8">
              <div className="text-body-sm">
                {payment.contact_name} · {payment.payment_type}
              </div>
              <div className="text-caption text-muted-foreground">
                {formatPaymentDate(payment.paid_on)}
              </div>
            </TableCell>
            <TableCell className="text-right tabular-nums">{payment.amount}</TableCell>
          </TableRow>
        ))}
      <TotalRow label={`Total for ${group.label}`} amount={group.total} indented />
    </Fragment>
  )
}

function TotalRow({
  label,
  amount,
  indented = false,
  grand = false,
}: {
  label: string
  amount: string
  indented?: boolean
  grand?: boolean
}) {
  return (
    <TableRow className={grand ? 'border-t-2 font-semibold hover:bg-transparent' : 'font-medium'}>
      <TableCell className={indented ? 'pl-8' : 'pl-3'}>{label}</TableCell>
      <TableCell className="text-right tabular-nums">{amount}</TableCell>
    </TableRow>
  )
}

function collectionsReportPath(selection: RangeSelection, exportCsv = false, owner?: string) {
  const params = new URLSearchParams({ period: selection.period })
  if (owner) params.set('owner', owner)
  if (selection.period === 'custom' && selection.startsOn && selection.endsOn) {
    params.set('starts_on', selection.startsOn)
    params.set('ends_on', selection.endsOn)
  }
  if (selection.grouping === 'month') params.set('grouping', selection.grouping)
  const endpoint = exportCsv ? '/api/v1/collections_report/export' : '/api/v1/collections_report'
  return `${endpoint}?${params}`
}

function collectionsReportError(error: Error) {
  if (!(error instanceof ApiError) || typeof error.body !== 'object' || error.body === null) {
    return 'Collections report unavailable'
  }
  const errors = (error.body as { errors?: Record<string, string[]> }).errors
  return errors
    ? (Object.values(errors).flat()[0] ?? 'Collections report unavailable')
    : 'Collections report unavailable'
}

const formatPaymentDate = displayShortDate
